/**
 * Klaviyo Back In Stock web component
 *
 * Usage:
 * <klaviyo-back-in-stock data-company-id="YOUR_KLAVIYO_COMPANY_ID">
 *   <div data-bis-dialog>
 *     <h3 data-bis-title></h3>
 *     <form data-bis-form>
 *       <input type="email" data-bis-email required />
 *       <input type="hidden" data-bis-variant />
 *       <input type="text" data-bis-bot tabindex="-1" autocomplete="off" />
 *       <div data-bis-message></div>
 *       <label>
 *         <input type="checkbox" data-bis-list-checkbox value="LIST_ID" />
 *         Also subscribe to newsletter
 *       </label>
 *       <button type="submit">Notify me</button>
 *     </form>
 *   </div>
 *
 *   <button
 *     type="button"
 *     data-bis-open-trigger
 *     data-bis-variant-id="47297605533992"
 *     data-bis-title-text="Moon Boot"
 *     data-bis-line1-label="Color: "
 *     data-bis-line1-value="Chocolate"
 *     data-bis-target="#my-bis-instance"
 *   >
 *     Notify me when available
 *   </button>
 *
 * <klaviyo-back-in-stock id="my-bis-instance" data-company-id="YOUR_KLAVIYO_COMPANY_ID">
 *   ...
 * </klaviyo-back-in-stock>
 *
 * Data hooks:
 * - data-company-id: Klaviyo company id (required)
 * - data-bis-success-message / data-bis-missing-company-message: theme copy (optional)
 * - data-bis-dialog: dialog container used by A11yDialog
 * - data-bis-form: BIS form
 * - data-bis-email: email input
 * - data-bis-variant: hidden input containing active variant id
 * - data-bis-title (h3): product title when opening popup
 * - data-bis-line1-label / data-bis-line1-value … line3: per-option rows (set on trigger)
 * - data-bis-message: feedback/error message container
 * - data-bis-bot: honeypot field (optional)
 * - data-bis-list-checkbox: newsletter list opt-in checkbox (optional)
 * - data-bis-open-trigger: opens popup and populates variant/title from trigger dataset
 *
 * Trigger datasets:
 * - data-bis-variant-id
 * - data-bis-title-text
 * - data-bis-line1-label … data-bis-line3-value (rows mirror product option1–3)
 * - data-bis-target (optional CSS selector for a specific component instance)
 *
 * Dependencies:
 * - window.klaviyoSubscribe(listId, email, payload?) from snippets/utility-script.liquid
 * - window.Pristine from snippets/utility-script.liquid
 * - window.sharedScrollLock from assets/noscroll.iife.js (body scroll lock on open/close)
 */
class KlaviyoBackInStock extends HTMLElement {
  connectedCallback() {
    this.selectors = {
      dialog: "[data-bis-dialog]",
      form: "[data-bis-form]",
      email: "[data-bis-email]",
      variant: "[data-bis-variant]",
      title: "h3[data-bis-title]",
      message: "[data-bis-message]",
      bot: "[data-bis-bot]",
      listCheckbox: "[data-bis-list-checkbox]",
      openTrigger: "[data-bis-open-trigger]"
    };

    this.initDialog();
    this.initForm();
    this.registerGlobalTriggers();
  }

  disconnectedCallback() {
    if (this._didLockBodyScroll && typeof window.sharedScrollLock?.enablePageScroll === "function") {
      window.sharedScrollLock.enablePageScroll();
      this._didLockBodyScroll = false;
    }
    KlaviyoBackInStock.instances.delete(this);
  }

  initDialog() {
    this.dialog = undefined;
    this._didLockBodyScroll = false;
    whenReady("A11yDialog", () => {
      const dialogEl = this.getDialogElement();
      if (dialogEl) {
        this.dialog = new window.A11yDialog(dialogEl);
        dialogEl.hidden = true;
        this.dialog.on("show", () => {
          dialogEl.hidden = false;
          const lock = window.sharedScrollLock;
          if (typeof lock?.disablePageScroll === "function") {
            lock.disablePageScroll();
            this._didLockBodyScroll = true;
            const scrollable = dialogEl.querySelector("[data-a11y-dialog-content]");
            if (scrollable && typeof lock.markScrollable === "function") {
              lock.markScrollable(scrollable);
            }
          }
        });
        this.dialog.on("hide", () => {
          dialogEl.hidden = true;
          dialogEl.classList.remove("active");
          if (this._didLockBodyScroll && typeof window.sharedScrollLock?.enablePageScroll === "function") {
            window.sharedScrollLock.enablePageScroll();
            this._didLockBodyScroll = false;
          }
        });
      }
    });
  }

  initForm() {
    this.form = this.querySelector(this.selectors.form);
    if (!this.form) return;

    if (typeof window.Pristine !== "function") {
      console.warn("window.Pristine is not available.");
      return;
    }

    if (this._bisPristineInitialized) return;
    this._bisPristineInitialized = true;

    this.pristine = new window.Pristine(
      this.form,
      {
        classTo: "bis-form-field",
        errorTextParent: "bis-form-field",
        errorTextClass: "bis-form-field__error",
        errorTextTag: "div",
        errorClass: "has-danger",
        successClass: "has-success"
      },
      false
    );

    const emailInput = this.form.querySelector(this.selectors.email);
    if (emailInput) {
      emailInput.addEventListener("input", () => {
        this.clearMessage();
        if (this.pristine) this.pristine.reset();
      });
    }

    if (this.form.dataset.klaviyoBisSubmitBound === "1") return;
    this.form.dataset.klaviyoBisSubmitBound = "1";
    this.form.addEventListener("submit", (e) => {
      e.preventDefault();
      this.handleSubmit();
    });
  }

  static resolveInstanceFromTrigger(trigger) {
    const targetSelector = trigger.dataset.bisTarget;
    if (targetSelector) {
      const target = document.querySelector(targetSelector);
      if (target && target.nodeName === "KLAVIYO-BACK-IN-STOCK") {
        return /** @type {KlaviyoBackInStock} */ (target);
      }
    }

    const closestInstance = trigger.closest("klaviyo-back-in-stock");
    if (closestInstance && closestInstance.nodeName === "KLAVIYO-BACK-IN-STOCK") {
      return /** @type {KlaviyoBackInStock} */ (closestInstance);
    }

    return KlaviyoBackInStock.instances.values().next().value;
  }

  registerGlobalTriggers() {
    KlaviyoBackInStock.instances.add(this);
    if (KlaviyoBackInStock.hasGlobalTriggerHandler) return;

    document.addEventListener("click", (event) => {
      const trigger = event.target.closest?.("[data-bis-open-trigger]");
      if (!trigger) return;

      const open = () => {
        const instance = KlaviyoBackInStock.resolveInstanceFromTrigger(trigger);
        if (!instance) return;

        const variantID = trigger.dataset.bisVariantId || "";
        const title = trigger.dataset.bisTitleText || "";
        instance.openPopup(variantID, title, trigger);
      };

      if (customElements.get("klaviyo-back-in-stock")) {
        open();
        return;
      }

      customElements.whenDefined("klaviyo-back-in-stock").then(open);
    });

    KlaviyoBackInStock.hasGlobalTriggerHandler = true;
  }

  getDialogElement() {
    return this.querySelector(this.selectors.dialog);
  }

  getCompanyID() {
    return this.dataset.companyId || "";
  }

  getSuccessMessage() {
    const s = (this.dataset.bisSuccessMessage || "").trim();
    return s || "You're in! We'll let you know when it's back.";
  }

  getMissingCompanyMessage() {
    const s = (this.dataset.bisMissingCompanyMessage || "").trim();
    return s || "Back in stock signup is not configured. Please contact us.";
  }

  clearMessage() {
    if (!this.form) return;
    const message = this.form.querySelector(this.selectors.message);
    if (!message) return;
    message.style.display = "none";
    message.innerHTML = "";
  }

  setMessage(content) {
    if (!this.form) return;
    const message = this.form.querySelector(this.selectors.message);
    if (!message) return;
    message.style.display = "block";
    message.innerHTML = content;
  }

  handleSubmit() {
    if (!this.form || !this.pristine) return;

    this.clearMessage();
    this.pristine.reset();

    const botInput = this.form.querySelector(this.selectors.bot);
    if (botInput && botInput.value.length) {
      return;
    }

    const valid = this.pristine.validate();
    if (!valid) {
      const fieldWrap = this.form.querySelector(".bis-form-field");
      if (fieldWrap) {
        const dupes = fieldWrap.querySelectorAll(".pristine-error");
        dupes.forEach((node, index) => {
          if (index > 0) node.remove();
        });
      }
      return;
    }

    const emailInput = this.form.querySelector(this.selectors.email);
    const variantInput = this.querySelector(this.selectors.variant);
    if (!emailInput || !variantInput) return;

    const email = emailInput.value;
    const variantID = variantInput.value;
    const listCheckbox = this.querySelector(this.selectors.listCheckbox);
    const listID = listCheckbox && listCheckbox.checked ? listCheckbox.value : undefined;
    const companyID = this.getCompanyID();

    if (!companyID) {
      this.setMessage(this.getMissingCompanyMessage());
      return;
    }

    this.handleKlaviyoBIS(email, variantID, companyID, listID);
  }

  onBisSuccess(email, listID) {
    this.setMessage(this.getSuccessMessage());
    if (listID === undefined) return;

    if (typeof window.klaviyoSubscribe !== "function") {
      console.warn("window.klaviyoSubscribe is not available.");
      return;
    }

    window.klaviyoSubscribe(listID, email).then(() => {
      console.log(`${email} SUBSCRIBED TO ${listID}`);
    });
  }

  handleKlaviyoBIS(email, variantID, companyID, listID) {
    const headers = new Headers();
    headers.append("revision", "2023-08-15");
    headers.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      data: {
        type: "back-in-stock-subscription",
        attributes: {
          profile: {
            data: {
              type: "profile",
              attributes: {
                email: `${email}`
              }
            }
          },
          channels: ["EMAIL"]
        },
        relationships: {
          variant: {
            data: {
              type: "catalog-variant",
              id: `$shopify:::$default:::${variantID}`
            }
          }
        }
      }
    });

    const requestOptions = {
      method: "POST",
      headers,
      body: raw,
      redirect: "follow"
    };

    this.clearMessage();

    fetch(`https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${companyID}`, requestOptions)
      .then((response) => response.text().then((body) => ({ response, body })))
      .then(({ response, body }) => {
        const trimmed = (body || "").trim();

        console.log("RESPONSE", response);
        console.log("BODY", trimmed);

        if (!trimmed) {
          if (response.ok) {
            this.onBisSuccess(email, listID);
            return;
          }
          throw new Error(`Back in stock request failed (${response.status})`);
        }

        const parsed = JSON.parse(trimmed);

        if (!parsed.errors) {
          this.onBisSuccess(email, listID);
          return;
        }

        this.setMessage(parsed.errors[0].detail);
      })
      .catch((error) => {
        console.error("Klaviyo back in stock signup failed:", error);
        this.setMessage("Something went wrong. Please try again.");
      });
  }

  /**
   * @param {HTMLElement | undefined} trigger
   */
  applyVariantLinesFromTrigger(trigger) {
    for (let line = 1; line <= 3; line += 1) {
      const labelAttr = `data-bis-line${line}-label`;
      const valueAttr = `data-bis-line${line}-value`;
      const labelEl = this.querySelector(`[${labelAttr}]`);
      const valueEl = this.querySelector(`[${valueAttr}]`);
      const row = labelEl?.closest(".back-in-stock__variant-line");

      let label = "";
      let value = "";
      if (trigger) {
        label = trigger.getAttribute(labelAttr) || "";
        value = trigger.getAttribute(valueAttr) || "";
      }

      if (labelEl) labelEl.textContent = label;
      if (valueEl) valueEl.textContent = value;
      if (row) {
        row.hidden = !label && !value;
      }
    }
  }

  openPopup(variantID, title, trigger) {
    const dialogEl = this.getDialogElement();
    const variantInput = this.querySelector(this.selectors.variant);
    const titleEl = this.querySelector(this.selectors.title);

    if (this.dialog) {
      this.dialog.show();
    }

    if (dialogEl) {
      dialogEl.classList.add("active");
    }
    if (variantInput) {
      variantInput.value = variantID;
    }
    if (titleEl && title) {
      titleEl.textContent = title;
    }
    this.applyVariantLinesFromTrigger(trigger);
  }
}

if (!customElements.get("klaviyo-back-in-stock")) {
  KlaviyoBackInStock.instances = new Set();
  KlaviyoBackInStock.hasGlobalTriggerHandler = false;
  customElements.define("klaviyo-back-in-stock", KlaviyoBackInStock);
}